import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { VIBE_CHAT_API_BASE } from '@/lib/vibeChatConfig';

export function isPrdPhaseReady(phase) {
  const p = String(phase || '').toUpperCase();
  return ['CONFIRM', 'GENERATE', 'REVISE', 'DONE'].includes(p);
}

function phaseMayProbePrd(phase) {
  return isPrdPhaseReady(phase);
}

/**
 * Resolved PRD readiness for UI — server hint, probe, or post-confirm phase.
 */
export function resolvePrdReady({ prdAvailable, prdAvailableHint, phase }) {
  return Boolean(prdAvailable || prdAvailableHint || isPrdPhaseReady(phase));
}

/**
 * Tracks whether GET /chat/{sessionId}/prd returns a document (S3/local).
 * Only probes once the session can have a PRD (CONFIRM+) or status already
 * hinted prd_available — avoids noisy 404s during GATHER.
 *
 * Once a PRD is confirmed for a session (probe 200, server hint, or CONFIRM+
 * phase), availability stays true until the session id changes.
 */
export function useChatPrdAvailable(
  sessionId,
  prdAvailableHint = false,
  intent = 'abap',
  phase = null,
  /** Keep probing when session had ABAP/static report but latest intent moved on (e.g. IS turn). */
  sessionSupportsPrd = false
) {
  const [prdAvailable, setPrdAvailable] = useState(false);
  const [prdChecking, setPrdChecking] = useState(false);
  const supportsPrd =
    sessionSupportsPrd ||
    ['abap', 'static_report'].includes(String(intent || '').toLowerCase());
  const phaseEligible = phaseMayProbePrd(phase);

  const markPrdAvailable = useCallback(() => {
    setPrdAvailable(true);
  }, []);

  useEffect(() => {
    setPrdAvailable(false);
    setPrdChecking(false);
  }, [sessionId]);

  useEffect(() => {
    if (supportsPrd && prdAvailableHint) {
      setPrdAvailable(true);
    }
  }, [sessionId, prdAvailableHint, supportsPrd]);

  // ABAP flow writes the PRD before CONFIRM — treat post-gather phases as ready
  // so the rail does not sit on "Preparing PRD…" waiting for a flaky GET /prd probe.
  useEffect(() => {
    if (supportsPrd && phaseEligible) {
      setPrdAvailable(true);
    }
  }, [sessionId, phaseEligible, supportsPrd]);

  useEffect(() => {
    if (!supportsPrd || !sessionId || !VIBE_CHAT_API_BASE) {
      return undefined;
    }

    const shouldProbe = Boolean(prdAvailableHint) || phaseEligible;
    if (!shouldProbe) {
      return undefined;
    }

    const base = VIBE_CHAT_API_BASE.replace(/\/$/, '');
    const url = `${base}/chat/${sessionId}/prd`;
    const probeSessionId = sessionId;
    let cancelled = false;

    const probe = async () => {
      setPrdChecking(true);
      try {
        const { status } = await axios.get(url, {
          responseType: 'arraybuffer',
          headers: {
            Accept: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          },
          timeout: 60000,
          validateStatus: (s) => s === 200 || s === 404,
        });
        if (cancelled || probeSessionId !== sessionId) return;
        setPrdAvailable((prev) => status === 200 || prev);
        return status === 200;
      } catch {
        return false;
      } finally {
        if (!cancelled && probeSessionId === sessionId) {
          setPrdChecking(false);
        }
      }
    };

    let timer;
    void (async () => {
      const found = await probe();
      if (cancelled || found) return;
      timer = setInterval(async () => {
        const ok = await probe();
        if (ok && timer) {
          clearInterval(timer);
          timer = undefined;
        }
      }, 5000);
    })();

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [sessionId, supportsPrd, prdAvailableHint, phaseEligible]);

  return { prdAvailable, prdChecking, markPrdAvailable };
}

/** Optional PRD tab — session-scoped; visible even when viewing a later non-PRD deliverable. */
export function shouldShowPrdArtifact(phase, prdAvailable = false) {
  if (!prdAvailable) return false;
  const p = String(phase || '').toUpperCase();
  return p === 'DONE' || p === 'REVISE';
}

/**
 * Right-rail "PRD document" pill — only for sessions that ever used a PRD-backed flow
 * (ABAP / static report), not Integration Suite–only threads.
 */
export function shouldShowPrdRailTab({
  phase,
  sessionSupportsPrd = false,
  prdAvailable = false,
  prdAvailableHint = false,
}) {
  if (!sessionSupportsPrd) return false;
  if (!Boolean(prdAvailable || prdAvailableHint)) return false;
  return shouldShowPrdArtifact(phase, true);
}
